import { ThemeProvider, css } from "@emotion/react";
import styled from "@emotion/styled";
import * as React from "react";
import { useSelector } from "react-redux";

import { Error } from "@livechat/design-system-icons";
import { DesignToken, Icon } from "@livechat/design-system-react-components";

import { BackButton, Grid, ViewContainer, ViewStack } from "components/grid";
import { SideNavigation } from "components/side-navigation/SideNavigation";
import { useBreakpoints } from "hooks/use-breakpoints";
import { contentPadding, contentPaddingSmall } from "routes/styled";
import { BrowserHistory } from "services/browser-history";
import { selectIsPastDue } from "store/entities/license";
import { withVar } from "themes/helpers";
import { legacyTheme } from "themes/legacyTheme";
import { getRoute } from "utils/routes";

interface IProps {
  containerTitle: string;
  content: React.ReactChild;
  focusedColumn: number;
  backButtonUrl?: string;
  subscriptionExpired?: boolean;
  smallPadding?: boolean;
  onSetFocusedColumn(focusedColumn: number): void;
}

export const SubscriptionLayout: React.FC<IProps> = ({
  containerTitle,
  content,
  subscriptionExpired,
  backButtonUrl,
  smallPadding,
  focusedColumn,
  onSetFocusedColumn,
}) => {
  const isPastDuePayment = useSelector(selectIsPastDue);
  const { isMobile } = useBreakpoints();

  const sideNavigationItems = [
    {
      id: "subscription",
      path: "/subscription",
      name: "Your subscription",
      exact: true,
    },
    {
      id: "invoices",
      path: "/subscription/invoices",
      name: "Invoices",
    },
    {
      id: "account-details",
      path: "/subscription/account-details",
      name: "Account details",
      customBadge: isPastDuePayment ? (
        <StyledErrorIcon source={Error} customColor={withVar(DesignToken.ContentBasicNegative)} />
      ) : null,
    },
  ];

  const calculateActiveNavigationItemId = (): string => {
    switch (getRoute()) {
      case "/subscription":
      case "/subscription/card":
      case "/subscription/shopify":
        return "subscription";

      case "/subscription/invoices":
        return "invoices";

      case "/subscription/account-details":
        return "account-details";

      default:
        return "";
    }
  };

  const handleNavigationItemClick = (): void => {
    onSetFocusedColumn(1);
  };

  const handleMobileBackButtonClick = (): void => {
    onSetFocusedColumn(0);
  };

  const handleBackButtonClick = (): void => {
    if (!backButtonUrl) {
      return;
    }
    BrowserHistory.push(backButtonUrl, "");
  };

  const contentWrapper = (
    <ThemeProvider theme={legacyTheme}>
      <ViewContainer
        title={containerTitle}
        placement="main-content"
        contentCss={smallPadding ? smallPaddingCss : normalPaddingCss}
        backButton={
          backButtonUrl ? (
            <BackButton onClick={handleBackButtonClick} />
          ) : isMobile ? (
            <BackButton onClick={handleMobileBackButtonClick} />
          ) : undefined
        }
        stackShowBackButton={!!backButtonUrl || isMobile}
      >
        {content}
      </ViewContainer>
    </ThemeProvider>
  );

  if (subscriptionExpired) {
    return contentWrapper;
  }

  return (
    <Grid relativeWidths={[1, 10]} minWidths={[280, 0]} focusedColumn={focusedColumn} overflowHidden>
      <ViewStack>
        <ThemeProvider theme={legacyTheme}>
          <ViewContainer
            contentCss={listContainer}
            title="Billing"
            backButton={<BackButton onClick={handleBackButtonClick} />}
          >
            <SideNavigation
              items={sideNavigationItems}
              activeItemId={calculateActiveNavigationItemId()}
              onItemClick={handleNavigationItemClick}
            />
          </ViewContainer>
        </ThemeProvider>
      </ViewStack>
      <ViewStack>{contentWrapper}</ViewStack>
    </Grid>
  );
};

const subscriptionContainer = css`
  overflow: auto;
  box-sizing: border-box;
`;

const normalPaddingCss = css`
  ${subscriptionContainer};
  ${contentPadding};
`;

const smallPaddingCss = css`
  ${subscriptionContainer};
  ${contentPaddingSmall};
`;

const listContainer = css`
  position: static;
`;

const StyledErrorIcon = styled(Icon)`
  display: flex;
  align-self: center;
  position: absolute;
  right: 0;
`;
